import { SearchMd } from "@untitledui/icons";
import type { ReactElement } from "react";
import { Input as SearchInput } from "@/components/Atoms/Input/input";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { RadioButtonBase } from "@/components/base/radio-buttons/radio-buttons";
import {
	Alert,
	AlertSemantic,
	AlertType,
} from "@/components/Molecules/Alert/Alert";
import {
	EmptyState,
	EmptyStateType,
} from "@/components/Molecules/EmptyState/EmptyState";
import { ErrorState } from "@/components/Molecules/EmptyState/ErrorState";
import {
	ListItem,
	ListItemAlignment,
	ListItemState,
	ListItemType,
} from "@/components/Molecules/ListItem/ListItem";
import { cx } from "@/utils/cx";

import { formatGeocodeCoordinates } from "./parse-map-coordinates";
import { SetLocationEsriAttribution } from "./SetLocationEsriAttribution";
import {
	type GeocodeCandidate,
	GeocodeSearchStatus,
	type GeocodeSearchStatus as GeocodeSearchStatusValue,
	SetLocationMethod,
	type SetLocationMethod as SetLocationMethodValue,
} from "./set-location-method";

/** Cap displayed geocode candidates (Figma Results list + Esri suggest). */
export const SET_LOCATION_MAX_GEOCODE_RESULTS = 5;

export interface SetLocationModalBodyProps {
	method: SetLocationMethodValue;
	onMethodChange: (method: SetLocationMethodValue) => void;
	geocodeLabel: string;
	coordinatesLabel: string;
	drawOnMapLabel: string;
	query: string;
	onQueryChange: (value: string) => void;
	searchPlaceholder: string;
	searchStatus: GeocodeSearchStatusValue;
	results: GeocodeCandidate[];
	selectedResultId: string | null;
	onSelectResult: (id: string) => void;
	resultsAriaLabel: string;
	emptySearchTitle: string;
	emptySearchDescription?: string;
	searchErrorTitle: string;
	searchErrorDescription?: string;
	retryLabel: string;
	onRetrySearch?: () => void;
	searchingLabel?: string;
	resolvingAddressLabel?: string;
	isGeocodingSelected?: boolean;
	esriAttributionLabel: string;
	showPointAlert?: boolean;
	pointAlertTitle: string;
	onDismissPointAlert?: () => void;
	/** Inline geocode rate-limit banner (Figma Limit reached). */
	showRateLimitAlert?: boolean;
	rateLimitAlertTitle?: string;
	rateLimitAlertDescription?: string;
	onDismissRateLimitAlert?: () => void;
	dismissAlertAriaLabel: string;
	latitude: string;
	longitude: string;
	onLatitudeChange: (value: string) => void;
	onLongitudeChange: (value: string) => void;
	latitudeLabel: string;
	longitudeLabel: string;
	latitudePlaceholder: string;
	longitudePlaceholder: string;
	latitudeError?: string;
	longitudeError?: string;
	isDisabled?: boolean;
	lockAlternateMethod?: boolean;
	/** When false, the Geocode method tab is disabled (catalog gate). @default true */
	isGeocodeSupported?: boolean;
	/** When false, the Coordinates method tab is disabled (catalog gate). @default true */
	isCoordinatesSupported?: boolean;
	/** When false, the Draw method tab is disabled. @default false */
	isDrawSupported?: boolean;
	/** When true, Draw would be valid but is feature-gated. @default false */
	isDrawGated?: boolean;
	drawComingSoonLabel?: string;
	/** Embedded draw map (only mounted when method is Draw). */
	drawPane?: ReactElement | null;
	showDeletedAlert?: boolean;
	deletedAlertTitle?: string;
	deletedAlertDescription?: string;
	undoLabel?: string;
	onUndoDelete?: () => void;
	onDismissDeletedAlert?: () => void;
}

/**
 * Figma `_location-modal-body` (34:2103) — method switcher plus geocode or coordinate pane.
 */
export function SetLocationModalBody({
	method,
	onMethodChange,
	geocodeLabel,
	coordinatesLabel,
	drawOnMapLabel,
	query,
	onQueryChange,
	searchPlaceholder,
	searchStatus,
	results,
	selectedResultId,
	onSelectResult,
	resultsAriaLabel,
	emptySearchTitle,
	emptySearchDescription,
	searchErrorTitle,
	searchErrorDescription,
	retryLabel,
	onRetrySearch,
	searchingLabel,
	resolvingAddressLabel,
	isGeocodingSelected = false,
	esriAttributionLabel,
	showPointAlert = false,
	pointAlertTitle,
	onDismissPointAlert,
	showRateLimitAlert = false,
	rateLimitAlertTitle,
	rateLimitAlertDescription,
	onDismissRateLimitAlert,
	dismissAlertAriaLabel,
	latitude,
	longitude,
	onLatitudeChange,
	onLongitudeChange,
	latitudeLabel,
	longitudeLabel,
	latitudePlaceholder,
	longitudePlaceholder,
	latitudeError,
	longitudeError,
	isDisabled = false,
	lockAlternateMethod = false,
	isGeocodeSupported = true,
	isCoordinatesSupported = true,
	isDrawSupported = false,
	isDrawGated = false,
	drawComingSoonLabel = "",
	drawPane = null,
	showDeletedAlert = false,
	deletedAlertTitle,
	deletedAlertDescription,
	undoLabel,
	onUndoDelete,
	onDismissDeletedAlert,
}: SetLocationModalBodyProps): ReactElement {
	const isGeocode = method === SetLocationMethod.Geocode;
	const isCoordinates = method === SetLocationMethod.Coordinates;
	const isDraw = method === SetLocationMethod.Draw;
	const selectedMethodClass = "ring-2 ring-brand_alt";
	const isGeocodeDisabled =
		isDisabled || !isGeocodeSupported || (lockAlternateMethod && !isGeocode);
	const isCoordinatesDisabled =
		isDisabled ||
		!isCoordinatesSupported ||
		(lockAlternateMethod && !isCoordinates);
	// Draw on map is not ready yet — keep draw plumbing; always disable the tab.
	const isDrawFeatureReady = false;
	const isDrawDisabled =
		isDisabled ||
		!isDrawFeatureReady ||
		!isDrawSupported ||
		(lockAlternateMethod && !isDraw);
	const showDrawComingSoon =
		isDrawGated && !isGeocodeSupported && !isCoordinatesSupported;

	const showResultsList =
		searchStatus === GeocodeSearchStatus.Results ||
		(searchStatus === GeocodeSearchStatus.Idle && results.length > 0);
	const visibleResults = results.slice(0, SET_LOCATION_MAX_GEOCODE_RESULTS);

	const deletedAlert = showDeletedAlert ? (
		<Alert
			type={AlertType.Alert}
			semantic={AlertSemantic.Error}
			title={deletedAlertTitle}
			description={deletedAlertDescription}
			primaryAction={
				onUndoDelete && undoLabel
					? { label: undoLabel, onPress: onUndoDelete }
					: undefined
			}
			onDismiss={onDismissDeletedAlert}
			dismissAriaLabel={dismissAlertAriaLabel}
			className="mt-auto w-full shrink-0"
			data-testid="set-location-deleted-alert"
		/>
	) : null;

	return (
		<div
			className="flex min-h-[500px] w-full flex-col items-start"
			data-testid="set-location-modal-body"
			data-method={method}
		>
			<div className="flex w-full items-start gap-2 bg-primary pb-4">
				<Button
					size="lg"
					color="secondary"
					className={cx("min-w-0 flex-1", isGeocode && selectedMethodClass)}
					aria-pressed={isGeocode}
					data-testid="set-location-method-geocode"
					isDisabled={isGeocodeDisabled}
					onClick={() => onMethodChange(SetLocationMethod.Geocode)}
				>
					{geocodeLabel}
				</Button>
				<Button
					size="lg"
					color="secondary"
					className={cx("min-w-0 flex-1", isCoordinates && selectedMethodClass)}
					aria-pressed={isCoordinates}
					data-testid="set-location-method-coordinates"
					isDisabled={isCoordinatesDisabled}
					onClick={() => onMethodChange(SetLocationMethod.Coordinates)}
				>
					{coordinatesLabel}
				</Button>
				<Button
					size="lg"
					color="secondary"
					className={cx("min-w-0 flex-1", isDraw && selectedMethodClass)}
					aria-pressed={isDraw}
					data-testid="set-location-method-draw"
					isDisabled={isDrawDisabled}
					onClick={() => onMethodChange(SetLocationMethod.Draw)}
				>
					{drawOnMapLabel}
				</Button>
			</div>

			{showDrawComingSoon ? (
				<>
					<p
						className="w-full text-sm text-tertiary"
						data-testid="set-location-draw-coming-soon"
					>
						{drawComingSoonLabel}
					</p>
					{deletedAlert}
				</>
			) : isGeocode ? (
				<div className="flex min-h-0 w-full flex-1 flex-col items-start">
					<SearchInput
						size="md"
						icon={SearchMd}
						placeholder={searchPlaceholder}
						value={query}
						onChange={onQueryChange}
						showClearButton
						clearButtonTestId="set-location-search-clear"
						isDisabled={isDisabled}
						data-testid="set-location-search"
						aria-label={searchPlaceholder}
					/>

					{searchStatus === GeocodeSearchStatus.Loading ? (
						<div
							className="flex min-h-[280px] w-full flex-1 flex-col items-center justify-center gap-4 py-8"
							data-testid="set-location-search-loading"
						>
							<LoadingIndicator type="line-spinner" size="md" />
							{searchingLabel ? (
								<p className="text-sm font-semibold text-secondary">
									{searchingLabel}
								</p>
							) : null}
						</div>
					) : null}

					{searchStatus === GeocodeSearchStatus.Empty ? (
						<EmptyState
							type={EmptyStateType.EmptySearch}
							title={emptySearchTitle}
							description={emptySearchDescription}
							className="min-h-[280px] flex-1 px-0 py-8"
						/>
					) : null}

					{searchStatus === GeocodeSearchStatus.Error ? (
						<ErrorState
							title={searchErrorTitle}
							description={searchErrorDescription}
							actionLabel={retryLabel}
							onAction={onRetrySearch}
							showAction={Boolean(onRetrySearch)}
							className="min-h-[280px] flex-1 px-0 py-8"
						/>
					) : null}

					{showResultsList ? (
						<div
							className="flex min-h-0 w-full flex-1 flex-col overflow-hidden"
							data-testid="set-location-results-panel"
						>
							<ul
								className="flex w-full flex-1 flex-col overflow-y-auto p-2"
								aria-label={resultsAriaLabel}
								data-testid="set-location-results"
							>
								{visibleResults.map((result) => {
									const isSelected = result.id === selectedResultId;
									const hasCoordinates =
										result.latitude != null &&
										result.longitude != null &&
										Number.isFinite(result.latitude) &&
										Number.isFinite(result.longitude);
									const isResolvingSelection =
										isSelected && isGeocodingSelected && !hasCoordinates;

									return (
										<li key={result.id} className="w-full">
											<ListItem
												type={ListItemType.List}
												alignment={ListItemAlignment.Middle}
												state={
													isSelected
														? ListItemState.Hover
														: ListItemState.Default
												}
												showOverline={false}
												trailing={null}
												primary={result.primary}
												leading={<RadioButtonBase isSelected={isSelected} />}
												supportingElements={
													isResolvingSelection ? (
														<p className="text-xs font-medium text-tertiary whitespace-nowrap">
															{resolvingAddressLabel}
														</p>
													) : hasCoordinates ? (
														<p className="text-xs font-medium text-tertiary whitespace-nowrap">
															{formatGeocodeCoordinates(
																result.latitude as number,
																result.longitude as number,
															)}
														</p>
													) : null
												}
												onPress={
													isDisabled
														? undefined
														: () => onSelectResult(result.id)
												}
											/>
										</li>
									);
								})}
							</ul>
						</div>
					) : null}

					{/* Idle / empty query: keep flex growth so Powered-by sits at bottom */}
					{searchStatus === GeocodeSearchStatus.Idle && !showResultsList ? (
						<div className="min-h-0 w-full flex-1" aria-hidden="true" />
					) : null}

					{showRateLimitAlert &&
					!showDeletedAlert &&
					rateLimitAlertTitle != null ? (
						<Alert
							type={AlertType.Alert}
							semantic={AlertSemantic.Error}
							title={rateLimitAlertTitle}
							description={rateLimitAlertDescription}
							onDismiss={onDismissRateLimitAlert}
							dismissAriaLabel={dismissAlertAriaLabel}
							className="w-full shrink-0"
							data-testid="set-location-rate-limit-alert"
						/>
					) : null}

					{showPointAlert && !showDeletedAlert && !showRateLimitAlert ? (
						<Alert
							type={AlertType.Alert}
							semantic={AlertSemantic.Info}
							title={pointAlertTitle}
							showDescription={false}
							onDismiss={onDismissPointAlert}
							dismissAriaLabel={dismissAlertAriaLabel}
							className="w-full shrink-0"
						/>
					) : null}

					{deletedAlert}

					{!showDeletedAlert ? (
						<SetLocationEsriAttribution label={esriAttributionLabel} />
					) : null}
				</div>
			) : isCoordinates ? (
				<div className="flex min-h-0 w-full flex-1 flex-col items-start">
					<div className="flex w-full items-start gap-3 bg-primary">
						<Input
							size="md"
							label={latitudeLabel}
							isRequired
							inputMode="decimal"
							placeholder={latitudePlaceholder}
							value={latitude}
							onChange={onLatitudeChange}
							isInvalid={Boolean(latitudeError)}
							hint={latitudeError}
							isDisabled={isDisabled}
							data-testid="set-location-latitude"
						/>
						<Input
							size="md"
							label={longitudeLabel}
							isRequired
							inputMode="decimal"
							placeholder={longitudePlaceholder}
							value={longitude}
							onChange={onLongitudeChange}
							isInvalid={Boolean(longitudeError)}
							hint={longitudeError}
							isDisabled={isDisabled}
							data-testid="set-location-longitude"
						/>
					</div>
					{deletedAlert}
				</div>
			) : (
				<div className="flex min-h-0 w-full flex-1 flex-col items-start gap-3">
					{drawPane}
					{deletedAlert}
				</div>
			)}
		</div>
	);
}
